/**
 * Number to its written form, in the six locales the survey player ships copy for.
 *
 * Ported verbatim from the `ntow.js` global that the legacy bundles loaded — the locale tables and
 * the French / Spanish / German joining rules are data, not something to re-derive, so the only
 * changes here are the module boundary and the name. `mode` is `'nth'` (ordinal) by default and
 * `'n'` gives the cardinal; an unsupported locale returns the number unchanged.
 */

export const numberToWords = (number, mode = 'nth', locale = 'en') => {
  const lTr = {
    it: {
      n: {
        _0: 'Zero',
        _1: 'Uno',
        _2: 'Due',
        _3: 'Tre',
        _4: 'Quattro',
        _5: 'Cinque',
        _6: 'Sei',
        _7: 'Sette',
        _8: 'Otto',
        _9: 'Nove',
        _10: 'Dieci',
        _11: 'Undici',
        _12: 'Dodici',
        _13: 'Tredici',
        _14: 'Quattordici',
        _15: 'Quindici',
        _16: 'Sedici',
        _17: 'Diciassette',
        _18: 'Diciotto',
        _19: 'Diciannove',
        _20: 'Venti',
        _30: 'Trenta',
        _40: 'Quaranta',
        _50: 'Cinquanta',
        _60: 'Sessanta',
        _70: 'Settanta',
        _80: 'Ottanta',
        _90: 'Novanta',
        _100: 'Cento',
        _200: 'Duecento',
        _300: 'Trecento',
        _400: 'Quattrocento',
        _500: 'Cinquecento',
        _600: 'Seicento',
        _700: 'Settecento',
        _800: 'Ottocento',
        _900: 'Novecento'
      }
    },
    de: {
      v: {
        zwanzig: true,
        dreißig: true,
        vierzig: true,
        fünfzig: true,
        sechzig: true,
        siebzig: true,
        achtzig: true,
        neunzig: true
      },
      n: {
        _0: 'null',
        _1: 'ein',
        _2: 'zwei',
        _3: 'drei',
        _4: 'vier',
        _5: 'fünf',
        _6: 'sechs',
        _7: 'sieben',
        _8: 'acht',
        _9: 'neun',
        _10: 'zehn',
        _11: 'elf',
        _12: 'zwölf',
        _13: 'dreizehn',
        _14: 'vierzehn',
        _15: 'fünfzehn',
        _16: 'sechzehn',
        _17: 'siebzehn',
        _18: 'achtzehn',
        _19: 'neunzehn',
        _20: 'zwanzig',
        _30: 'dreißig',
        _40: 'vierzig',
        _50: 'fünfzig',
        _60: 'sechzig',
        _70: 'siebzig',
        _80: 'achtzig',
        _90: 'neunzig',
        _100: 'einhundert',
        _200: 'zweihundert',
        _300: 'dreihundert',
        _400: 'vierhundert',
        _500: 'fünfhundert',
        _600: 'sechshundert',
        _700: 'siebenhundert',
        _800: 'achthundert',
        _900: 'neunhundert'
      }
    },
    fr: {
      n: {
        _0: 'zéro',
        _1: 'un',
        _2: 'deux',
        _3: 'trois',
        _4: 'quatre',
        _5: 'cinq',
        _6: 'six',
        _7: 'sept',
        _8: 'huit',
        _9: 'heuf',
        _10: 'dix',
        _11: 'onze',
        _12: 'douze',
        _13: 'treize',
        _14: 'quatorze',
        _15: 'quinze',
        _16: 'seize',
        _20: 'vingt',
        _30: 'trente',
        _40: 'quarante',
        _50: 'cinquante',
        _60: 'soixante',
        _70: 'soixante-dix',
        _71: 'soixante et onze',
        _72: 'soixante-douze',
        _73: 'soixante-treize',
        _74: 'soixante-quatorze',
        _75: 'soixante-quinze',
        _76: 'soixante-seize',
        _77: 'soixante-dix-sept',
        _78: 'soixante-dix-huit',
        _79: 'soixante-dix-neuf',
        _80: 'quatre-vingts',
        _90: 'quatre-vingts-dix',
        _97: 'quatre-vingt-dix-sept',
        _98: 'quatre-vingt-dix-huit',
        _99: 'quatre-vingt-dix-neuf',
        _100: 'cent',
        _200: 'deux cent',
        _300: 'trois cent',
        _400: 'quatre cent',
        _500: 'cinq cent',
        _600: 'six cent',
        _700: 'sept cent',
        _800: 'huit cent',
        _900: 'heuf cent'
      },
      nth: {}
    },
    sp: {
      n: {
        _0: 'cero',
        _1: 'uno',
        _2: 'dos',
        _3: 'tres',
        _4: 'cuarto',
        _5: 'cinco',
        _6: 'seis',
        _7: 'siete',
        _8: 'ocho',
        _9: 'nueve',
        _10: 'diez',
        _11: 'once',
        _12: 'doce',
        _13: 'trece',
        _14: 'catorce',
        _15: 'quince',
        _16: 'dieciséis',
        _17: 'diecisiete',
        _18: 'dieciocho',
        _19: 'diecinueve',
        _20: 'veinte',
        _30: 'treinta',
        _40: 'cuarenta',
        _50: 'cincuenta',
        _60: 'sesenta',
        _70: 'setenta',
        _80: 'ochenta',
        _90: 'noventa',
        _100: 'cien',
        _200: 'doscientos',
        _300: 'trescientos',
        _400: 'cuatrocientos',
        _500: 'quinientos',
        _600: 'seiscientos',
        _700: 'setecientos',
        _800: 'ochocientos',
        _900: 'novecientos'
      },
      nth: {}
    },
    en: {
      n: {
        _0: 'Zero',
        _1: 'One',
        _2: 'Two',
        _3: 'Three',
        _4: 'Four',
        _5: 'Five',
        _6: 'Six',
        _7: 'Seven',
        _8: 'Eight',
        _9: 'Nine',
        _10: 'Ten',
        _11: 'Eleven',
        _12: 'Twelve',
        _13: 'Thirteen',
        _14: 'Fourteen',
        _15: 'Fifteen',
        _16: 'Sixteen',
        _17: 'Seventeen',
        _18: 'Eighteen',
        _19: 'Nineteen',
        _20: 'Twenty',
        _30: 'Thirty',
        _40: 'Forty',
        _50: 'Fifty',
        _60: 'Sixty',
        _70: 'Seventy',
        _80: 'Eighty',
        _90: 'Ninety',
        _100: 'One Hundred',
        _200: 'Two Hundred',
        _300: 'Three Hundred',
        _400: 'Four Hundred',
        _500: 'Five Hundred',
        _600: 'Six Hundred',
        _700: 'Seven Hundred',
        _800: 'Eight Hundred',
        _900: 'Nine Hundred'
      },
      nth: {
        _0: 'Zero',
        _1: 'First',
        _2: 'Second',
        _3: 'Third',
        _4: 'Fourth',
        _5: 'Fifth',
        _6: 'Sixth',
        _7: 'Seventh',
        _8: 'Eighth',
        _9: 'Ninth',
        _10: 'Tenth',
        _11: 'Eleventh',
        _12: 'Twelfth',
        _13: 'Thirteenth',
        _14: 'Fourteenth',
        _15: 'Fifteenth',
        _16: 'Sixteenth',
        _17: 'Seventeenth',
        _18: 'Eighteenth',
        _19: 'Nineteenth',
        _20: 'Twentieth',
        _30: 'Thirtieth',
        _40: 'Fortieth',
        _50: 'Fiftieth',
        _60: 'Sixtieth',
        _70: 'Seventieth',
        _80: 'Eightieth',
        _90: 'Ninetieth',
        _100: 'One Hundredth',
        _200: 'Two Hundredth',
        _300: 'Three Hundredth',
        _400: 'Four Hundredth',
        _500: 'Five Hundredth',
        _600: 'Six Hundredth',
        _700: 'Seven Hundredth',
        _800: 'Eight Hundredth',
        _900: 'Nine Hundredth'
      }
    },
    ru: {
      n: {
        _0: 'Ноль',
        _1: 'Один',
        _2: 'Два',
        _3: 'Три',
        _4: 'Четыре',
        _5: 'Пять',
        _6: 'Шесть',
        _7: 'Семь',
        _8: 'Восемь',
        _9: 'Девять',
        _10: 'Десять',
        _11: 'Одиннадцать',
        _12: 'Двенадцать',
        _13: 'Тринадцать',
        _14: 'Четырнадцать',
        _15: 'Пятнадцать',
        _16: 'Шеснадцать',
        _17: 'Семнадцать',
        _18: 'Восемнадцать',
        _19: 'Девятнадцать',
        _20: 'Двадцать',
        _30: 'Тридцать',
        _40: 'Сорок',
        _50: 'Пятьдесят',
        _60: 'Шестьдесят',
        _70: 'Сесьдесят',
        _80: 'Восемьдесят',
        _90: 'Девяносто',
        _100: 'Сто',
        _200: 'Двести',
        _300: 'Триста',
        _400: 'Четыреста',
        _500: 'Пятьсот',
        _600: 'Шестьсот',
        _700: 'Семьсот',
        _800: 'Восемьсот',
        _900: 'Девятьсот'
      },
      nth: {
        _0: 'Нулевой',
        _1: 'Первый',
        _2: 'Второй',
        _3: 'Третий',
        _4: 'Четвертый',
        _5: 'Пятый',
        _6: 'Шесой',
        _7: 'Седьмой',
        _8: 'Восьмой',
        _9: 'Девятый',
        _10: 'Десятый',
        _11: 'Одиннадцатый',
        _12: 'Двенадцатый',
        _13: 'Тринадцатый',
        _14: 'Четырнадцатый',
        _15: 'Пятнадцатый',
        _16: 'Шеснадцатый',
        _17: 'Семнадцатый',
        _18: 'Восемнадцатый',
        _19: 'Девятнадцатый',
        _20: 'Двадцатый',
        _30: 'Тридцатый',
        _40: 'Сороковой',
        _50: 'Пятьдесятый',
        _60: 'Шестьдесятый',
        _70: 'Сесьдесятый',
        _80: 'Восемьдесятый',
        _90: 'Девяностый',
        _100: 'Сотый',
        _200: 'Двухсотый',
        _300: 'Трехсотый',
        _400: 'Четырсотый',
        _500: 'Пятисотый',
        _600: 'Шестисотый',
        _700: 'Семисотый',
        _800: 'Восьмисотый',
        _900: 'Девятисотый'
      },
      ntime: {
        _1: 'Одна',
        _2: 'Две'
      }
    }
  }

  function nParser(number) {
    if (Number(number) < 10) return getN(1, Number(number))
    if (Number(number) < 100) return getN(10, Number(number))
    if (Number(number) < 1000) return getN(100, Number(number))
    return String(number)
  }

  function getN(b, number) {
    const ret = getPreN(number)

    if (ret) {
      if (mode != 'n') return lTr[locale][mode]['_' + number] || ret
      else return ret
    } else {
      return lTr[locale]['n']['_' + b * Math.floor(number / b)] + ' ' + nParser(number % b)
    }
  }

  function getPreN(number) {
    let ret = null
    if (locale == 'fr' && number >= 17 && number <= 99) {
      if (lTr['fr']['n']['_' + number]) ret = lTr['fr']['n']['_' + number]
      else {
        let delimiter
        let n1
        let n2
        if (number >= 17 && number <= 69) {
          delimiter = number % 10 == 1 ? ' et ' : '-'
          n1 = Number(String(number)[0]) * 10
          n2 = Number(String(number)[1])

          ret = lTr['fr']['n']['_' + n1] + delimiter + lTr['fr']['n']['_' + n2].toLowerCase()
        }

        if (number >= 80 && number <= 99) {
          delimiter = number == 81 ? ' et ' : '-'
          ret = 'Quatre-vingt' + delimiter + lTr['fr']['n']['_' + (number - 80)].toLowerCase()
        }
      }
    } else if (locale == 'sp' && number > 20 && number < 100 && number % 10 != 0) {
      const n1 = Number(String(number)[0]) * 10
      const n2 = Number(String(number)[1])

      ret = lTr['sp']['n']['_' + n1] + ' y ' + lTr['sp']['n']['_' + n2].toLowerCase()
    } else ret = lTr[locale]['n']['_' + number]

    return ret
  }

  if (locale != 'en' && locale != 'ru' && locale != 'fr' && locale != 'sp' && locale != 'de') return String(number)
  else {
    let ret = nParser(number)

    if (locale == 'fr') ret = ret[0].toUpperCase() + ret.slice(1) //capitalize

    if (locale == 'de') {
      const ret_sp = ret.split(' ')
      if (ret_sp.length > 1 && lTr['de']['v'][ret_sp[ret_sp.length - 2]]) {
        const tmp = ret_sp[ret_sp.length - 2]
        ret_sp[ret_sp.length - 2] = ret_sp[ret_sp.length - 1]
        ret_sp[ret_sp.length - 1] = 'und' + tmp
      }
      ret = ret_sp.join('')
    }

    if (locale == 'it') ret = ret.split(' ').join('')

    return ret
  }
}

/**
 * Seconds as a spoken minutes phrase, with the half-minute and plural rules each locale needs.
 * Ported alongside `numberToWords`, which it calls for the number itself.
 */
export const numberToMinutes = (seconds, locale = 'en') => {
  const mode = locale == 'ru' ? 'ntime' : 'n'

  const minutes = Math.floor(seconds / 60)
  const str = numberToWords(minutes, mode, locale)

  function secondsToMinutesWord(s, l, half) {
    let str = ''
    const minutes = Math.floor(s / 60)
    const halfOn = half && (seconds % 60) - 30 >= 0

    switch (l) {
      case 'en':
        if (halfOn) str = 'and a half '
        str += 'minute' + (s < 120 ? '' : 's')
        break
      case 'ru':
        if (halfOn) str = 'с половиной '
        str += 'минут'
        const mod10 = minutes % 10
        if (mod10 == 1) str += 'а'
        if (mod10 == 2 || mod10 == 3 || mod10 == 4) str += 'ы'
        break
      case 'fr':
        str = s < 120 ? 'minute' : 'minutes'
        if (halfOn) str += ' et demie'
        break
      case 'sp':
        str = s < 120 && s > 0 ? 'minuto' : 'minutos'
        if (halfOn) str += ' y medio'
        break
      case 'de':
        if (halfOn) str = 'und eine halbe Minute'
        else str = s < 120 && s > 0 ? 'Minute' : 'Minuten'
        break
      case 'it':
        str = s < 120 && s > 0 ? 'minuto' : 'minuti'
        if (halfOn) str += ' e mezzo'
        break
    }
    return str
  }

  let prefix = ''
  if (locale == 'de') prefix = 'Noch '

  return prefix + str + ' ' + secondsToMinutesWord(seconds, locale, true)
}
